import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Combobox } from "../components/ui/combobox";
import { MultiSelect } from "../components/ui/multi-select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { ChevronLeft, Loader2 } from "lucide-react";
import { Skeleton } from "../components/ui/skeleton";
import { Procedure } from "../types";
import { userHasAccessToDepartment } from "../utils/procedure-permissions";
import api from "../utils/api";

const cadenceOptions = [
  "Daily",
  "Weekly",
  "Bi-Weekly",
  "Monthly",
  "Quarterly",
  "Annually",
  "As needed",
  "Ongoing"
];

export default function ProcedureFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const { departments, users, procedures, addProcedure, updateProcedure } = useData();
  const { currentUser } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [procedure, setProcedure] = useState<Partial<Procedure>>({
    procedureName: "",
    procedureCadence: "",
    procedureOwner: "",
    procedureStakeholders: [],
    viewers: [],
    departmentId: "",
  });

  // Helper to normalize procedure data into form state
  const populateForm = (sourceProcedure: Procedure) => {
    // Resolve department ID: prefer populated department.id, otherwise lookup by departmentCode
    let deptId = sourceProcedure.department?.id || "";
    if (!deptId && sourceProcedure.departmentId) {
      const foundDept = departments.find(d => d.departmentCode === sourceProcedure.departmentId);
      if (foundDept) deptId = foundDept.id;
    }

    setProcedure({
      id: sourceProcedure.id,
      procedureName: sourceProcedure.procedureName,
      procedureCadence: sourceProcedure.procedureCadence,
      procedureOwner: typeof sourceProcedure.procedureOwner === 'string'
        ? sourceProcedure.procedureOwner
        : sourceProcedure.procedureOwner?.id || "",
      procedureStakeholders: sourceProcedure.procedureStakeholders?.map(s =>
        typeof s === 'string' ? s : s.id
      ) || [],
      viewers: sourceProcedure.viewers?.map(v =>
        typeof v === 'string' ? v : v.id
      ) || [],
      departmentId: deptId,
      videoEmbed: sourceProcedure.videoEmbed,
    });
  };

  // Load existing procedure data in edit mode
  useEffect(() => {
    if (!isEditMode || !id) return;

    const existingProcedure = procedures.find(p => p.id == id);
    if (existingProcedure) {
      populateForm(existingProcedure);
      return;
    }

    // If not in context, fetch from API
    const fetchProcedure = async () => {
      setIsFetching(true);
      try {
        const data = await api.get(`/api/procedures/${id}`);
        if (data.success && data.procedure) {
          populateForm(data.procedure);
        } else {
          toast.error("Procedure not found");
          navigate("/procedure");
        }
      } catch (error) {
        console.error("Error fetching procedure:", error);
        toast.error("Failed to load procedure");
        navigate("/procedure");
      } finally {
        setIsFetching(false);
      }
    };

    fetchProcedure();
  }, [isEditMode, id, procedures, departments, navigate]);

  const accessibleDepartments = departments.filter(
    dept => currentUser && userHasAccessToDepartment(currentUser, dept.departmentCode)
  );

  const handleSubmit = async () => {
    if (!procedure.procedureName || !procedure.procedureCadence || !procedure.procedureOwner || !procedure.departmentId) {
      toast.error("All required fields must be filled");
      return;
    }

    setIsLoading(true);

    try {
      if (isEditMode && id) {
        await updateProcedure(id, {
          procedureName: procedure.procedureName,
          procedureCadence: procedure.procedureCadence,
          procedureOwner: procedure.procedureOwner,
          procedureStakeholders: procedure.procedureStakeholders,
          viewers: procedure.viewers,
          departmentId: procedure.departmentId,
          videoEmbed: procedure.videoEmbed,
        }, true);
        navigate(`/procedure/${id}`);
      } else {
        await addProcedure({
          procedureName: procedure.procedureName,
          procedureCadence: procedure.procedureCadence,
          procedureOwner: procedure.procedureOwner,
          procedureStakeholders: procedure.procedureStakeholders || [],
          viewers: procedure.viewers || [],
          departmentId: procedure.departmentId,
          videoEmbed: procedure.videoEmbed,
          department: null,
          activities: [],
          createdBy: currentUser?.id || "",
          createdAt: new Date().toISOString().split('T')[0],
        } as unknown as Omit<Procedure, "id">);
        navigate("/procedure");
      }
    } catch (error) {
      console.error("Failed to save procedure:", error);
      toast.error(isEditMode ? "Failed to update procedure" : "Failed to add procedure");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (isEditMode && id) {
      navigate(`/procedure/${id}`);
    } else {
      navigate("/procedure");
    }
  };

  return (
    <div className="space-y-6 p-2 md:p-0 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="outline" size="icon" onClick={handleCancel}>
          <ChevronLeft size={16} />
        </Button>
        <h1 className="text-2xl font-bold">
          {isEditMode ? "Edit Procedure" : "Add Procedure"}
        </h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {isEditMode ? "Procedure Details" : "New Procedure"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {isFetching && (
            <div className="space-y-6">
              <div className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-10 w-full" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-10 w-full" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-10 w-full" />
                </div>
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-10 w-full" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-10 w-full" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-10 w-full" />
              </div>
            </div>
          )}

          {!isFetching && (<>
          {/* Procedure Name */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="procedureName">
                Procedure Name <span className="text-red-500">*</span>
              </Label>
              <p className={`text-sm ${(procedure.procedureName?.length || 0) > 100 ? 'text-red-500' : 'text-muted-foreground'}`}>
                Character limit {procedure.procedureName?.length || 0} / 100
              </p>
            </div>
            <Input
              id="procedureName"
              placeholder="Enter procedure name"
              value={procedure.procedureName || ""}
              onChange={(e) => setProcedure({ ...procedure, procedureName: e.target.value })}
              maxLength={100}
              disabled={isFetching}
            />
          </div>

          {/* Cadence & Department */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="procedureCadence">
                Cadence <span className="text-red-500">*</span>
              </Label>
              <Select
                value={procedure.procedureCadence || ""}
                onValueChange={(value) => setProcedure({ ...procedure, procedureCadence: value })}
                disabled={isFetching}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select cadence" />
                </SelectTrigger>
                <SelectContent>
                  {cadenceOptions.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="departmentId">
                Department <span className="text-red-500">*</span>
              </Label>
              <Select
                value={procedure.departmentId || ""}
                onValueChange={(value) => setProcedure({ ...procedure, departmentId: value })}
                disabled={isFetching}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  {accessibleDepartments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.id}>
                      {dept.departmentCode} - {dept.departmentName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Procedure Owner */}
          <div className="space-y-2">
            <Label htmlFor="procedureOwner">
              Procedure Owner <span className="text-red-500">*</span>
            </Label>
            <Combobox
              options={Array.from(new Map(users.map(u => [`${u.name}-${u.jobRole}`, u])).values())
                .filter(u => ['Worker', 'Team Lead', 'Supervisor', 'Manager', 'Senior Manager', 'Director', 'Admin', 'Account Owner'].includes(u.userRole))
                .filter(u => !u.archived)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(user => ({
                  label: `${user.name} (${user.jobRole || 'none'})`,
                  value: user.id,
                }))}
              value={(procedure.procedureOwner as string) || ""}
              onChange={(value) => setProcedure({ ...procedure, procedureOwner: value || "" })}
              placeholder="Select owner"
              searchPlaceholder="Search owner..."
              emptyMessage="No user found"
            />
          </div>

          {/* Stakeholders */}
          <div className="space-y-2">
            <div className="flex justify-between items-center mb-1">
              <Label>Stakeholders</Label>
              <Badge variant="outline">{(procedure.procedureStakeholders as string[])?.length || 0} Selected</Badge>
            </div>

            {/* Selected Stakeholders Badges */}
            {procedure.procedureStakeholders && (procedure.procedureStakeholders as string[]).length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3 p-3 bg-slate-50/30 rounded-md border border-dashed border-gray-300">
                {(procedure.procedureStakeholders as string[]).map((userId) => {
                  const user = users.find(u => String(u.id) === String(userId));
                  if (!user) return null;
                  return (
                    <Badge 
                      key={userId} 
                      variant="secondary" 
                      className="pl-3 pr-1 py-1 flex items-center gap-1 bg-white border shadow-sm group"
                    >
                      <span className="text-sm font-medium">{user.name} ({user.jobRole || 'none'})</span>
                      <button
                        type="button"
                        onClick={() => {
                          const updated = (procedure.procedureStakeholders as string[]).filter(id => id !== userId);
                          setProcedure({ ...procedure, procedureStakeholders: updated });
                        }}
                        className="ml-1 p-0.5 hover:bg-gray-100 rounded-full text-muted-foreground hover:text-destructive transition-colors"
                      >
                        <span className="sr-only">Remove</span>
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                      </button>
                    </Badge>
                  );
                })}
              </div>
            )}

            <MultiSelect
              options={Array.from(new Map(users.map(u => [`${u.name}-${u.jobRole}`, u])).values())
                .filter(u => !u.archived)
                .map(user => ({
                value: user.id,
                label: `${user.name} (${user.jobRole || 'none'})`
              }))}
              selected={procedure.procedureStakeholders as string[] || []}
              onChange={(values) => setProcedure({ ...procedure, procedureStakeholders: values })}
              placeholder="Select stakeholders"
              maxDisplayTags={1}
            />
          </div>

          {/* Viewers */}
          <div className="space-y-2">
            <div className="flex justify-between items-center mb-1">
              <Label>Viewers</Label>
              <Badge variant="outline">{(procedure.viewers as string[])?.length || 0} Selected</Badge>
            </div>

            {/* Selected Viewers Badges */}
            {procedure.viewers && (procedure.viewers as string[]).length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3 p-3 bg-slate-50/30 rounded-md border border-dashed border-gray-300">
                {(procedure.viewers as string[]).map((userId) => {
                  const user = users.find(u => String(u.id) === String(userId));
                  if (!user) return null;
                  return (
                    <Badge 
                      key={userId} 
                      variant="secondary" 
                      className="pl-3 pr-1 py-1 flex items-center gap-1 bg-white border shadow-sm group"
                    >
                      <span className="text-sm font-medium">{user.name} ({user.jobRole || 'none'})</span>
                      <button
                        type="button"
                        onClick={() => {
                          const updated = (procedure.viewers as string[]).filter(id => id !== userId);
                          setProcedure({ ...procedure, viewers: updated });
                        }}
                        className="ml-1 p-0.5 hover:bg-gray-100 rounded-full text-muted-foreground hover:text-destructive transition-colors"
                      >
                        <span className="sr-only">Remove</span>
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                      </button>
                    </Badge>
                  );
                })}
              </div>
            )}

            <MultiSelect
              options={Array.from(new Map(users.map(u => [`${u.name}-${u.jobRole}`, u])).values())
                .filter(u => !u.archived)
                .map(user => ({
                value: user.id,
                label: `${user.name} (${user.jobRole || 'none'})`
              }))}
              selected={procedure.viewers as string[] || []}
              onChange={(values) => setProcedure({ ...procedure, viewers: values })}
              placeholder="Select viewers"
              maxDisplayTags={1}
            />
          </div>

          {/* Video Embed */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="videoEmbed">SharePoint Video Embed</Label>
              <span className="text-xs text-muted-foreground">Optional</span>
            </div>
            <Textarea
              id="videoEmbed"
              placeholder="Paste SharePoint embed iframe code here..."
              value={procedure.videoEmbed || ""}
              onChange={(e) => setProcedure({ ...procedure, videoEmbed: e.target.value })}
              className="min-h-[100px] font-mono text-sm"
              disabled={isFetching}
            />
            <p className="text-xs text-muted-foreground">
              Go to SharePoint → open the video → click Embed → copy the iframe code and paste it here.
              Users will see the video player without the raw URL.
            </p>
          </div>
          </>)}
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={handleCancel} disabled={isLoading || isFetching}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={isLoading || isFetching}>
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {isEditMode ? "Saving..." : "Adding..."}
            </>
          ) : (
            isEditMode ? "Save Changes" : "Add Procedure"
          )}
        </Button>
      </div>
    </div>
  );
}
